import { ModalBox, Spinner } from '@zappyrent/hundop';
import { isEmpty, upperFirst } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import shallow from 'zustand/shallow';

import Header from '../../components/Layout/Header';
import Main from '../../components/Layout/Main';
import UpdatePaymentInfoModal from '../../components/UploadDocuments/UpdateIbanModal';
import UserDocuments from '../../components/User/UserDocuments';
import UserInfoFormFields from '../../components/User/UserInfoFormFields';
import { IUserInfoFieldsState } from '../../components/User/UserInfoFormFields/UserInfoFormFields.types';
import UserLegalInfoFields from '../../components/User/UserLegalInfoFields';
import { IUserLegalInfoFieldsState } from '../../components/User/UserLegalInfoFields/UserLegalInfoFields.types';
import { useToastContext } from '../../contexts/ToastProvider';
import { useUser } from '../../shared/hooks';
import useUserDocuments from '../../shared/hooks/useUserDocuments';
import { IUser } from '../../shared/ts/user';
import useStore from '../../store';
import {
  ButtonContainer,
  StyledButton,
  StyledFooter,
  StyledMissingFieldLabel,
  StyledMissingFieldLabelsContainer,
  StyledMissingFieldTitle,
  StyledMissingFieldWrapper,
  StyledMissingFieldsText,
  StyledTitle,
  StyledWrapper,
} from './PersonalInformation.styles';
import {
  IMissingDocumentInformation,
  IMissingInformation,
} from './PersonalInformation.types';
import { validateUserInfoFormFields } from './PersonalInformation.utils';

const PersonalInformation = (): JSX.Element | null => {
  const isLoadingUser = useUser();
  const { user, updateUserById } = useStore(
    state => ({
      user: state.user,
      updateUserById: state.updateUserById,
    }),
    shallow,
  );
  const toast = useToastContext();
  const isLoadingUserDocuments = useUserDocuments(user?.id ?? null);
  const { t } = useTranslation(['navigation', 'form', 'translation']);

  const defaultFormErrorLabel = t('form:requiredField');

  const [userInfoFields, setUserInfoFields] = useState<IUserInfoFieldsState[]>(
    [],
  );
  const [userLegalInfoFields, setUserLegalInfoFields] = useState<
    IUserLegalInfoFieldsState[]
  >([]);

  const [canSaveUserInfo, setCanSaveUserInfo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [updatedUser, setUpdatedUser] = useState<Partial<IUser>>({});
  const [missingPersonalInformation, setMissingPersonalInformation] =
    useState<IMissingInformation | null>(null);
  const [missingUserDocument, setMissingUserDocument] =
    useState<IMissingDocumentInformation | null>(null);
  const [missingUserLegalInformation, setMissingUserLegalInformation] =
    useState<IMissingInformation | null>(null);

  const { mutateAsync, isLoading } = useMutation(async () => {
    try {
      const payload = {
        ...updatedUser,
      } as IUser;
      if (payload && user?.id) {
        await updateUserById(user.id, payload);
        toast.success(upperFirst(t('translation:profileUpdated')));
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(upperFirst(t('translation:profileUpdateError')));
    }
    if (user?.type !== 'tenant') {
      setIsModalOpen(true);
    }
  });

  const canSaveUserInfoNoError = useMemo(() => {
    const userInfoFieldsHasError = userInfoFields.some(
      userInfoField => userInfoField.hasErrors,
    );
    const is_legal_entity =
      updatedUser?.is_legal_entity ?? user?.is_legal_entity;
    const userLegalInfoFieldsHasError =
      is_legal_entity === 1
        ? userLegalInfoFields.some(
            userLegalInfoField => userLegalInfoField.hasErrors,
          )
        : false;
    return !userInfoFieldsHasError && !userLegalInfoFieldsHasError;
  }, [userInfoFields, userLegalInfoFields, updatedUser, user]);

  const saveUserInfo = () => {
    mutateAsync();
  };

  const handleUserChange = (newFields: IUser) => {
    setCanSaveUserInfo(true);
    setUpdatedUser({
      ...updatedUser,
      ...newFields,
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (user) {
      const missingFields = validateUserInfoFormFields(user);
      const userInfoFieldsBase = [
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.first_name ?? '',
          isEditing: false,
          label: upperFirst(t('form:firstName')),
          name: 'first_name',
          type: 'text',
          value: user?.first_name ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.last_name ?? '',
          isEditing: false,
          label: upperFirst(t('form:lastName')),
          name: 'last_name',
          type: 'text',
          value: user?.last_name ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.email ?? '',
          isEditing: false,
          label: upperFirst(t('form:email')),
          name: 'email',
          type: 'email',
          value: user?.email ?? '',
        },
        {
          errorLabel: t('form:phoneIsNotValid'),
          hasErrors: false,
          initValue: user?.phone_no ?? '',
          isEditing: false,
          label: upperFirst(t('form:phoneNumber')),
          name: 'phone_no',
          type: 'tel',
          value: user?.phone_no ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.birthday ?? '',
          isEditing: false,
          label: upperFirst(t('form:dateOfBirth')),
          name: 'birthday',
          type: 'date',
          value: user?.birthday ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.place_of_birth ?? '',
          isEditing: false,
          label: upperFirst(t('form:cityOfBirth')),
          name: 'place_of_birth',
          type: 'text',
          value: user?.place_of_birth ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.city ?? '',
          isEditing: false,
          label: upperFirst(t('form:cityOfResidence')),
          name: 'city',
          type: 'text',
          value: user?.city ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.country ?? '',
          isEditing: false,
          label: upperFirst(t('form:country')),
          name: 'country',
          type: 'text',
          value: user?.country ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.residence_province ?? '',
          isEditing: false,
          label: upperFirst(t('form:province')),
          name: 'residence_province',
          type: 'text',
          value: user?.residence_province ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.residence_address ?? '',
          isEditing: false,
          label: upperFirst(t('form:residenceAddress')),
          name: 'residence_address',
          type: 'text',
          value: user?.residence_address ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.residence_postcode ?? '',
          isEditing: false,
          label: upperFirst(t('form:postalCode')),
          name: 'residence_postcode',
          type: 'text',
          value: user?.residence_postcode ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.id_document.number ?? '',
          isEditing: false,
          label: upperFirst(
            user.type === 'tenant'
              ? t('form:identityDocument')
              : t('form:identityDocumentLL'),
          ),
          name: 'id_document_number',
          type: 'text',
          value: user?.id_document.number ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.id_document.expiry_date ?? '',
          isEditing: false,
          label: upperFirst(t('form:documentExpiryDate')),
          name: 'id_document_expiry_date',
          type: 'date',
          value: user?.id_document?.expiry_date ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.tax_code ?? '',
          isEditing: false,
          label: upperFirst(t('form:taxCode')),
          name: 'tax_code',
          type: 'text',
          value: user?.tax_code ?? '',
        },
      ] as IUserInfoFieldsState[];
      const userLegalInfoFieldsBase = [
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_name ?? '',
          isEditing: false,
          label: upperFirst(t('form:companyName')),
          name: 'company_name',
          type: 'text',
          value: user?.company_name ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.vat_number ?? '',
          isEditing: false,
          label: upperFirst(t('form:vatNumber')),
          name: 'vat_number',
          type: 'text',
          value: user?.vat_number ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_city ?? '',
          isEditing: false,
          label: upperFirst(t('form:city')),
          name: 'company_city',
          type: 'text',
          value: user?.company_city ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_country ?? '',
          isEditing: false,
          label: upperFirst(t('form:country')),
          name: 'company_country',
          type: 'text',
          value: user?.company_country ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_province ?? '',
          isEditing: false,
          label: upperFirst(t('form:province')),
          name: 'company_province',
          type: 'text',
          value: user?.company_province ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_address ?? '',
          isEditing: false,
          label: upperFirst(t('form:residenceAddress')),
          name: 'company_address',
          type: 'text',
          value: user?.company_address ?? '',
        },
        {
          errorLabel: defaultFormErrorLabel,
          hasErrors: false,
          initValue: user?.company_postcode ?? '',
          isEditing: false,
          label: upperFirst(t('form:postalCode')),
          name: 'company_postcode',
          type: 'text',
          value: user?.company_postcode ?? '',
        },
      ] as IUserLegalInfoFieldsState[];
      setUserInfoFields(
        userInfoFieldsBase.map(item =>
          missingFields.includes(item.name)
            ? { ...item, isEditing: true, hasErrors: true }
            : { ...item },
        ),
      );
      setUserLegalInfoFields(
        userLegalInfoFieldsBase.map(item =>
          missingFields.includes(item.name)
            ? { ...item, isEditing: true, hasErrors: true }
            : { ...item },
        ),
      );
    }
  }, [user]);

  return (
    <>
      {!isLoadingUser && !isLoadingUserDocuments && (
        <StyledWrapper>
          <Header />
          <Main>
            <StyledTitle>{t('translation:personalInformation')}</StyledTitle>
            <br />
            {!isEmpty(missingPersonalInformation?.fields) ||
            !isEmpty(missingUserDocument?.fields) ||
            !isEmpty(missingUserLegalInformation?.fields) ? (
              <StyledMissingFieldWrapper>
                <StyledMissingFieldTitle>
                  {upperFirst(t('translation:missingInfo'))}:
                </StyledMissingFieldTitle>

                {missingPersonalInformation &&
                  !isEmpty(missingPersonalInformation?.fields) && (
                    <StyledMissingFieldLabelsContainer>
                      <StyledMissingFieldLabel>
                        {upperFirst(t(missingPersonalInformation.label))}:
                      </StyledMissingFieldLabel>
                      {missingPersonalInformation.fields.map((field, index) => (
                        <StyledMissingFieldsText
                          key={`missing-field-personal-info-${index}`}
                        >
                          {upperFirst(t(field))}
                        </StyledMissingFieldsText>
                      ))}
                    </StyledMissingFieldLabelsContainer>
                  )}

                {missingUserDocument && !isEmpty(missingUserDocument?.fields) && (
                  <StyledMissingFieldLabelsContainer>
                    <StyledMissingFieldLabel>
                      {upperFirst(t(missingUserDocument.label))}:
                    </StyledMissingFieldLabel>
                    {missingUserDocument.fields.map((field, index) => (
                      <StyledMissingFieldsText
                        key={`missing-field-user-document-${index}`}
                      >
                        {field.sideLabel
                          ? `${upperFirst(t(field.label))}/${upperFirst(
                              t(field.sideLabel),
                            )}`
                          : upperFirst(t(field.label))}
                      </StyledMissingFieldsText>
                    ))}
                  </StyledMissingFieldLabelsContainer>
                )}

                {missingUserLegalInformation &&
                  !isEmpty(missingUserLegalInformation?.fields) && (
                    <StyledMissingFieldLabelsContainer>
                      <StyledMissingFieldLabel>
                        {upperFirst(t(missingUserLegalInformation.label))}:
                      </StyledMissingFieldLabel>
                      {missingUserLegalInformation.fields.map(
                        (field, index) => (
                          <StyledMissingFieldsText
                            key={`missing-field-user-legal-information-${index}`}
                          >
                            {upperFirst(t(field))}
                          </StyledMissingFieldsText>
                        ),
                      )}
                    </StyledMissingFieldLabelsContainer>
                  )}
              </StyledMissingFieldWrapper>
            ) : (
              <></>
            )}
            <UserInfoFormFields
              user={user!}
              onChange={handleUserChange}
              fields={userInfoFields}
              setFields={setUserInfoFields}
              setMissingPersonalInformation={setMissingPersonalInformation}
            />

            <UserDocuments setMissingUserDocument={setMissingUserDocument} />
            <UserLegalInfoFields
              user={user}
              onChange={handleUserChange}
              fields={userLegalInfoFields}
              setFields={setUserLegalInfoFields}
              setMissingUserLegalInformation={setMissingUserLegalInformation}
            />
            <br />
            <ButtonContainer>
              {isLoading ? (
                <Spinner />
              ) : (
                <StyledButton
                  user={user?.type}
                  onClick={saveUserInfo}
                  disabled={
                    !canSaveUserInfo || !canSaveUserInfoNoError || isLoading
                  }
                >
                  {' '}
                  {isLoading && <Spinner size={15} strokeSize={2} />}
                  {t('form:save')}
                </StyledButton>
              )}
            </ButtonContainer>
            <StyledFooter />
          </Main>
        </StyledWrapper>
      )}
      {isModalOpen && (
        <ModalBox
          ariaLabel={t('a11y:close')}
          open={isModalOpen}
          handleCloseModal={closeModal}
        >
          <UpdatePaymentInfoModal onClose={closeModal} />{' '}
        </ModalBox>
      )}
    </>
  );
};

export default PersonalInformation;
